import { Icon } from '@ant-design/compatible';
import { Button, Pagination as Paging, Popover, Row, Table, Tag, Space, Select, Typography } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { CustomLink } from '../../../components/CustomLink';
import { CustomSider } from '../../../components/layout/CustomSider';
import { Section, SectionRow } from '../../../components/Section';
import { SectionTitle } from '../../../components/text/Titles';
import { getInboxFileStatus } from '../../../lib/util/status';
import useGlobal from '../../../store/Store';
import { Inbox, InboxFileRow, InboxFilters, Pagination } from '../../../types';
import {
  DateFilter,
  FlowFilter,
  ScoreFilter,
  StatusFilter,
  TagFilter,
  SorterToggle,
  initialFilterValues,
  NameFilter,
} from '../Filters';
import { Shorten } from '../../../components/text/Shorten';

const { Text } = Typography;

type Props = {
  readOnly?: boolean;
  selectedId: null | string;
  inbox: Inbox | undefined;
  inboxList: InboxFileRow[] | null;
  filterItemsCount: number;
  loading: boolean;
  paginationObject: Pagination;
  setPaginationObject: any;
  onClickUrl: (fileId: string) => any;
  filters: InboxFilters;
  setFilters: any;
  flowOptions: any;
};

const InboxMenuSideBar = (props: Props) => {
  const {
    selectedId,
    inbox,
    onClickUrl,
    inboxList,
    filterItemsCount,
    loading,
    paginationObject,
    setPaginationObject,
    filters,
    setFilters,
    flowOptions,
  } = props;

  const history = useHistory();

  const onClick = useCallback(
    url => {
      history.push(onClickUrl(url));
    },
    [onClickUrl, history]
  );

  const [globalState] = useGlobal();

  const [datasetLength, setDatasetLength] = useState<number>(0);
  const [lastClickedDirection, setLastClickedDirection] = useState<string>('right');
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [secondColumnKey, setSecondColumnKey] = useState<string>('tag');

  const keyPress = useCallback(
    (event: any): void => {
      if (event.code === 'ArrowRight' && inboxList && !globalState.tagEdit) {
        setLastClickedDirection('right');
        inboxList.map((file, i) => {
          if (file.inbox_id === selectedId && i === inboxList.length - 1) {
            //need to go to next page, unless last page has been reached
            if (paginationObject.page !== Math.ceil(datasetLength / paginationObject.pageSize) - 1) {
              setPaginationObject((prev: any) => ({ ...prev, page: prev.page + 1 }));
            }
          } else if (file.inbox_id === selectedId) onClick(inboxList[i + 1].inbox_id);
          return null;
        });
      }

      if (event.code === 'ArrowLeft' && inboxList && !globalState.tagEdit) {
        setLastClickedDirection('left');
        inboxList.map((file, i) => {
          if (file.inbox_id === selectedId && i === 0) {
            //need to go to previous page, unless we are on the first page
            if (paginationObject.page !== 0) {
              setPaginationObject((prev: any) => ({ ...prev, page: prev.page - 1 }));
            }
          } else if (file.inbox_id === selectedId) onClick(inboxList[i - 1].inbox_id);
          return null;
        });
      }
    },
    [inboxList, selectedId, onClick, globalState.tagEdit, datasetLength, paginationObject, setPaginationObject]
  );

  useEffect(() => {
    window.addEventListener('keyup', keyPress);
    return () => {
      window.removeEventListener('keyup', keyPress);
    };
  }, [inboxList, keyPress]);

  useEffect(() => {
    if (inboxList && filterItemsCount) {
      setDatasetLength(filterItemsCount);
      if (inboxList.length === 0) {
        return;
      }
      if (inboxList.filter(file => file.inbox_id === selectedId).length === 0) {
        lastClickedDirection === 'right'
          ? onClick(inboxList[0].inbox_id)
          : onClick(inboxList[inboxList.length - 1].inbox_id);
        setLastClickedDirection('right');
      }
    }
  }, [inboxList, filterItemsCount, selectedId, setDatasetLength, lastClickedDirection, onClick, paginationObject]);

  const noFiltersActivated = () => {
    return !filters.tag && !filters.date.length && !filters.flow.length && !filters.score.length && !filters.status;
  };

  const isFilterKeyActive = useCallback(
    (filterKey: string) => {
      if (filters[filterKey as keyof {}]) {
        if (Array.isArray(filters[filterKey as keyof {}])) {
          return (filters[filterKey as keyof {}] as any[]).length;
        }
        return true;
      } else {
        return false;
      }
    },
    [filters]
  );

  return (
    <CustomSider width={350} border="right" style={{ paddingTop: '1.25rem' }}>
      <Button
        className="absolute top-2 right-4 z-20"
        onClick={() => {
          setShowFilters(prev => !prev);
        }}>
        <Icon type={`${showFilters ? 'up' : 'down'}`} />
      </Button>
      {showFilters && (
        <Section>
          <Row className="w-full justify-between items-center">
            <SectionTitle fontSize={16}>Filters</SectionTitle>
            {!noFiltersActivated() ? (
              <Button className="mr-14 mb-2 pr-0" onClick={() => setFilters(initialFilterValues)} type="link">
                Reset all
                <Icon type="close" />
              </Button>
            ) : null}
          </Row>
          <SectionRow className="relative">
            <Space wrap>
              <Popover
                trigger="click"
                placement="bottom"
                content={<NameFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Name
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('name') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<DateFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Date
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('date') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<TagFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Tag
                  <Icon type="filter" theme="filled" style={{ color: isFilterKeyActive('tag') ? '#00b9ff' : '#ddd' }} />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<FlowFilter filters={filters} setFilters={setFilters} flowOptions={flowOptions} />}>
                <Button>
                  Flow
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('flow') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<StatusFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Status
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('status') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
              <Popover
                trigger="click"
                placement="bottom"
                content={<ScoreFilter filters={filters} setFilters={setFilters} />}>
                <Button>
                  Score
                  <Icon
                    type="filter"
                    theme="filled"
                    style={{ color: isFilterKeyActive('score') ? '#00b9ff' : '#ddd' }}
                  />
                </Button>
              </Popover>
            </Space>
          </SectionRow>
          <SectionTitle fontSize={16}>Sort</SectionTitle>
          <SectionRow>
            <Space wrap>
              <SorterToggle filterKey="reference_run_id" label="Name" filters={filters} setFilters={setFilters} />
              <SorterToggle filterKey="time_created" label="Date" filters={filters} setFilters={setFilters} />
              <SorterToggle filterKey="tag" label="Tag" filters={filters} setFilters={setFilters} />
              <SorterToggle filterKey="tag_count" label="Tag Count" filters={filters} setFilters={setFilters} />
              <SorterToggle filterKey="score" label="Score" filters={filters} setFilters={setFilters} />
            </Space>
          </SectionRow>
        </Section>
      )}
      <Row className="px-5 justify-between">
        <SectionTitle fontSize={16}>Inbox files</SectionTitle>
        {!noFiltersActivated() ? (
          <Text className="mr-14">
            {filterItemsCount} / {inbox?.nrofdocumentsforclass} Results
          </Text>
        ) : (
          <Text className="mr-14">{inbox?.nrofdocumentsforclass} Results</Text>
        )}
      </Row>
      <Row className="w-full">
        {inboxList ? (
          <Table
            className="ant-table-toolbox w-full"
            columns={[
              {
                title: 'Date & Name',
                key: 'fileName',
                dataIndex: 'fileName',
                render: (text: string, inboxFile: InboxFileRow) => (
                  <div style={{ paddingLeft: '.5em' }}>
                    <CustomLink
                      style={{ fontWeight: inboxFile.inbox_id === selectedId ? 'bold' : 'normal' }}
                      onClickUrl={onClickUrl(inboxFile.inbox_id)}
                      disabled={inboxFile.inbox_status === 'PROCESSING'}>
                      <span>{moment(inboxFile.inbox_time_created).format('ll HH:mm')}</span>
                      <div style={{ color: '#999', fontSize: '12px' }}>
                        <Shorten text={inboxFile.inbox_reference_run_id} digits={20} />
                      </div>
                    </CustomLink>
                  </div>
                ),
              },
              {
                title: (
                  <Select
                    style={{ minWidth: '120px' }}
                    value={secondColumnKey}
                    onChange={setSecondColumnKey}
                    dropdownMatchSelectWidth={false}>
                    {['tag', 'tag count', 'multiflow name', 'status', 'score'].map(column => {
                      return (
                        <Select.Option key={column} value={column}>
                          {column[0].toUpperCase() + column.slice(1)}
                        </Select.Option>
                      );
                    })}
                  </Select>
                ),
                key: 'inbox_' + secondColumnKey.replace(/ /g, '_'),
                dataIndex: 'inbox_' + secondColumnKey.replace(/ /g, '_'),
                render: (text: string, inboxFile: InboxFileRow) => (
                  <span>
                    {secondColumnKey === 'tag' ? (
                      <Tag>
                        <Shorten text={text} digits={12} />
                      </Tag>
                    ) : secondColumnKey === 'score' ? (
                      <span>{Math.floor(parseFloat(text) * 100)}</span>
                    ) : secondColumnKey === 'status' ? (
                      <Tag color={getInboxFileStatus(inboxFile).color}>{text}</Tag>
                    ) : (
                      <span>{text}</span>
                    )}
                  </span>
                ),
              },
            ]}
            rowKey={record => record.inbox_id}
            showHeader={true}
            loading={loading}
            dataSource={inboxList}
            pagination={false}
          />
        ) : (
          <></>
        )}
        <Row className="w-full my-4 justify-center">
          {filterItemsCount ? (
            <Paging
              size="small"
              showSizeChanger={false}
              current={paginationObject.page + 1}
              pageSize={paginationObject.pageSize}
              total={filterItemsCount}
              onChange={page => {
                setPaginationObject({ ...paginationObject, page: page - 1 });
              }}
            />
          ) : (
            <></>
          )}
        </Row>
      </Row>
    </CustomSider>
  );
};

export default InboxMenuSideBar;
